import { z } from 'zod';

// ─── ai.describe ─────────────────────────────────────────────────────────────

export const aiDescribeRequestedSchema = z.object({
  wallpaper_id: z.number().int().positive(),
  prompt_id_override: z.number().int().positive().optional(),
  lang_code: z.string().min(2).max(5).default('pl'),
  image_url: z.string().url(),
});

// ─── ai.categorize ───────────────────────────────────────────────────────────

export const aiCategorizeRequestedSchema = z.object({
  wallpaper_id: z.number().int().positive(),
  lang_code: z.string().min(2).max(5).default('pl'),
});

// ─── ai.seo ──────────────────────────────────────────────────────────────────
//
// SEO step = per-wallpaper SEO variant generation for a SET of locales.
// The actual work is done by the seo-variant-invalidated handler.
// This event is an orchestrator: when received, the handler resolves the
// locale set and emits ONE wallpaper.variant.invalidated carrying all locales.
//
// Use this when you want to (re)generate SEO for the whole wallpaper at once
// or for a specific subset of locales.

export const aiSeoRequestedSchema = z.object({
  wallpaper_id: z.number().int().positive(),
  /**
   * Optional locale set filter. When omitted (or empty), the orchestrator
   * resolves the union of supported_langs across all active sites.
   * When provided, only these locales are processed in a single LLM call.
   */
  lang_codes: z.array(z.string().min(2).max(5)).optional(),
  /**
   * Force regeneration of locales that are already current. The orchestrator
   * marks the resolved wallpapers_i18n rows is_stale=true so the variant
   * handler's idempotency guard is forced to regenerate them.
   *
   * - false (default): only missing / already-stale locales are generated —
   *   the cost-saving "skip if fresh" path. An explicit user run without force
   *   is treated like a retry and skips already-current locales.
   * - true: the user explicitly asked to overwrite existing content.
   */
  force: z.boolean().optional().default(false),
});

// ─── Payload types ────────────────────────────────────────────────────────────

export type AiDescribeRequestedPayload = z.infer<typeof aiDescribeRequestedSchema>;
export type AiCategorizeRequestedPayload = z.infer<typeof aiCategorizeRequestedSchema>;
export type AiSeoRequestedPayload = z.infer<typeof aiSeoRequestedSchema>;
